import React from 'react';
import sanitizeHtml from 'sanitize-html';
import { showConfirmAntd, ShowConfirmAntdProps } from 'src/components/show-modal-antd';
import { apiFetch, fetchData } from 'src/shared/web-api-utils';

interface Notification {
  id: number;
  title: string;
  content: string;
}

export async function dismissNotification(notification: Notification) {
  await apiFetch(`notifications/${notification.id}/seen`, { method: 'POST' });
}

export async function showUnreadNotifications() {
  await fetchData<Notification>(
    'notifications/unread',
    async (notifications) => {
      for (const notification of notifications ?? []) {
        if (!(await showNotification(notification, { onConfirm: () => dismissNotification(notification) }))) {
          break;
        }
      }
    },
    () => undefined,
    'Failed to get unread notifications',
  );
}

export async function showNotification(notification: { title: string; content: string }, props?: Partial<ShowConfirmAntdProps>) {
  const substituted = substitutePlaceholders(notification);
  return await showConfirmAntd(
    'notification',
    substituted.title,
    <div className="notification" dangerouslySetInnerHTML={{ __html: sanitize(substituted.content, true) }} />,
    {
      width: 600,
      showCancel: false,
      ...props,
    },
  );
}

export function substitutePlaceholder(text: string) {
  return text.replace(/\$APPLICATION_NAME/g, window.applicationName);
}

export function substitutePlaceholders(notification: { title: string; content: string }) {
  return {
    ...notification,
    title: substitutePlaceholder(notification.title),
    content: substitutePlaceholder(notification.content),
  };
}

/**
 * Note we properly sanitise the HTML in the backend on retrieval anyway, so this is only here to make the preview strip out any HTML inserted into the editor the back-end will reject anyway.
 */
export function sanitize(html: string, stripWhitespace = false) {
  let sanitized = sanitizeHtml(html, {
    allowedTags: ['b', 'i', 'strong', 'em', 'p', 'div', 'span', 'ul', 'ol', 'li', 'br', 'pre'],
    allowedAttributes: {},
  });
  sanitized = sanitized.replace(/<br ?\/>/g, '<br>');
  if (stripWhitespace) {
    const whitespaceRegex = '(<p>(&nbsp;|\\s)+</p>(\r?\n)?)+';
    sanitized = sanitized.replace(`^${whitespaceRegex}`, '').replace(`${whitespaceRegex}$`, '');
  }
  return sanitized;
}
